import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import AdelaidaPortrait, { framesForVoice } from "@/components/AdelaidaPortrait";
import { api } from "@/lib/apiClient";
import { getPlayer } from "@/lib/sharedAudio";
import { computeEnvelope, envAt } from "@/lib/orbVoice";
import { Info, Volume2, Square } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Adelaida explains a Reclaim area — recorded once, so playing it costs nothing.
export default function LearnMore({ area, className = "", onCta }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [talking, setTalking] = useState(false);
  const amplitudeRef = useRef(0);
  const speakingRef = useRef(false);
  const rafRef = useRef(null);
  const tokenRef = useRef(0);
  const ctxRef = useRef(null);
  const urlRef = useRef(null);
  const openRef = useRef(false);

  const stop = () => {
    tokenRef.current += 1;
    cancelAnimationFrame(rafRef.current);
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    try { getPlayer().pause(); } catch {}
    speakingRef.current = false; amplitudeRef.current = 0; setTalking(false);
  };

  const play = async (d) => {
    if (!d?.audio_url) return;
    stop();
    const my = tokenRef.current;
    const buf = await (await fetch(`${BACKEND_URL}${d.audio_url}`)).arrayBuffer();
    let env = null;
    try {
      ctxRef.current = ctxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      env = computeEnvelope(await ctxRef.current.decodeAudioData(buf.slice(0)));
    } catch {}
    if (my !== tokenRef.current || !openRef.current) return;
    const el = getPlayer();
    urlRef.current = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
    el.src = urlRef.current;
    el.onended = stop;
    speakingRef.current = true; setTalking(true);
    const drive = () => {
      if (el.paused || el.ended) return;
      const a = envAt(env, el.currentTime);
      amplitudeRef.current = Math.pow(Math.max(0, Math.min(1, a)), 0.7);
      rafRef.current = requestAnimationFrame(drive);
    };
    el.play().then(() => { rafRef.current = requestAnimationFrame(drive); }).catch(stop);
  };

  const openSheet = async () => {
    setOpen(true); openRef.current = true;
    try { const { data } = await api.get(`/learn-more/${area}`); setInfo(data); if (openRef.current) play(data); } catch { setInfo({ script: "" }); }
  };

  useEffect(() => () => { stop(); try { ctxRef.current?.close(); } catch {} }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <button onClick={openSheet} data-testid={`learn-more-${area}`}
        className={`inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors ${className}`}>
        <Info className="w-3.5 h-3.5" /> Learn more
      </button>
      <Dialog open={open} onOpenChange={(o) => { openRef.current = o; if (!o) stop(); setOpen(o); }}>
        <DialogContent className="max-w-md rounded-3xl border-white/10 bg-[#0d1426]" data-testid={`learn-more-sheet-${area}`}>
          <DialogTitle className="sr-only">About {info?.title || area}</DialogTitle>
          <div className="flex items-center gap-4">
            <div className={`relative w-20 h-20 rounded-full overflow-hidden shrink-0 ring-1 transition-shadow duration-700 ${talking ? "ring-primary/60 shadow-[0_0_36px_rgba(120,170,230,0.35)]" : "ring-white/10"}`}>
              <AdelaidaPortrait amplitudeRef={amplitudeRef} speakingRef={speakingRef} frames={framesForVoice("sage")} status={talking ? "speaking" : "idle"} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-primary">Adelaida · your Coach</p>
              <p className="font-serif text-2xl leading-tight">{info?.title || "…"}</p>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line max-h-[45vh] overflow-y-auto" data-testid="learn-more-text">
            {info ? (info.script || "Adelaida's introduction to this area is coming soon.") : "…"}
          </p>
          {info?.tagline && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid="learn-more-ui">
              <p className="text-[10px] uppercase tracking-[0.22em] text-primary">{info.title}</p>
              <p className="text-sm mt-1">{info.tagline}</p>
            </div>
          )}
          {info?.cta && info?.script && (
            <button onClick={() => { stop(); openRef.current = false; setOpen(false); onCta?.(); }} data-testid="learn-more-cta"
              className="w-full rounded-full bg-primary text-primary-foreground py-3 text-sm font-medium hover:opacity-90 transition-opacity">{info.cta}</button>
          )}
          {info?.audio_url && (
            <button onClick={() => (talking ? stop() : play(info))} data-testid="learn-more-play"
              className="self-start inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {talking ? <><Square className="w-3.5 h-3.5" /> Stop</> : <><Volume2 className="w-3.5 h-3.5" /> Hear Adelaida</>}
            </button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
